import { ref, set, update, onValue, remove } from "firebase/database";
import React, { useEffect, useState } from "react";
import { RiEdit2Fill } from "react-icons/ri";
import { MdDelete } from "react-icons/md";
import { FaArrowDown, FaArrowUp, FaPlus } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { database } from "../firebaseConfig";
import { setError, setLoading } from "../redux/slices/ui";
import Question from "../components/ui/Question";
import PageHeader from "../components/ui/PageHeader";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import { Input } from "../components/ui/Field";
import { eur, todayISO } from "../utils/format";

const DailyReportForm = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [date, setDate] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // 'expense' | 'income'
  const [modalData, setModalData] = useState({ description: "", amount: "" });
  const [modalIndex, setModalIndex] = useState(null);
  const [isDateReadOnly, setIsDateReadOnly] = useState(false);

  const [showQuestion, setShowQuestion] = useState(false);
  const [showDeleteQuestion, setShowDeleteQuestion] = useState(false);
  const [existingReportData, setExistingReportData] = useState(null);

  useEffect(() => {
    if (key) {
      dispatch(setLoading(true));
      const reportRef = ref(database, `daily-reports/${key}`);
      onValue(
        reportRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            setDate(key);
            setExpenses(data.expenses || []);
            setIncomes(data.incomes || []);
            setIsDateReadOnly(true);
          } else {
            navigate("/404");
          }
          dispatch(setLoading(false));
        },
        { onlyOnce: true },
      );
    }
  }, [key]);

  // New report → default the date to today (editable). Routed through the same
  // duplicate-date check so a pre-filled today still warns if today already exists.
  useEffect(() => {
    if (!key) checkDateSubmission(todayISO());
  }, []);

  const checkDateSubmission = (selectedDate) => {
    const reportRef = ref(database, `daily-reports/${selectedDate}`);
    onValue(
      reportRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setExistingReportData({ ...data, date: selectedDate });
          setShowQuestion(true);
          setDate(selectedDate);
        } else {
          setDate(selectedDate);
          setIsDateReadOnly(false);
          setExistingReportData(null);
        }
      },
      { onlyOnce: true },
    );
  };

  const handleConfirmQuestion = () => {
    if (existingReportData) {
      setDate(existingReportData.date);
      setExpenses(existingReportData.expenses || []);
      setIncomes(existingReportData.incomes || []);
      setIsDateReadOnly(true);
    }
    setShowQuestion(false);
  };

  const openModal = (type, item = null, index = null) => {
    setModalType(type);
    setModalIndex(index);
    setModalData(item ? { description: item.description, amount: item.amount } : { description: "", amount: "" });
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleModalInputChange = (field, value) => setModalData((prev) => ({ ...prev, [field]: value }));

  const handleSaveItem = () => {
    const newItem = { description: modalData.description, amount: parseFloat(modalData.amount) };
    if (modalType === "expense") {
      if (modalIndex !== null) {
        const updated = [...expenses];
        updated[modalIndex] = newItem;
        setExpenses(updated);
      } else {
        setExpenses((prev) => [...prev, newItem]);
      }
    } else if (modalType === "income") {
      if (modalIndex !== null) {
        const updated = [...incomes];
        updated[modalIndex] = newItem;
        setIncomes(updated);
      } else {
        setIncomes((prev) => [...prev, newItem]);
      }
    }
    closeModal();
  };

  const deleteItem = () => {
    if (modalType === "expense") setExpenses((prev) => prev.filter((_, i) => i !== modalIndex));
    else if (modalType === "income") setIncomes((prev) => prev.filter((_, i) => i !== modalIndex));
    closeModal();
  };

  const calculateTotal = (items) => items.reduce((acc, curr) => acc + curr.amount, 0).toFixed(2);

  const calculateNetTotal = () =>
    (parseFloat(calculateTotal(incomes)) - parseFloat(calculateTotal(expenses))).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      dispatch(setError({ show: true, message: "Παρακαλώ εισάγετε την ημερομηνία." }));
      return;
    }
    if (date == existingReportData?.date && !isDateReadOnly) {
      dispatch(
        setError({
          show: true,
          message: "Έχει γίνει απολογισμός για την συγκεκριμένη ημέρα. Επιλέξτε διαφορετική ημ/νία",
        }),
      );
      return;
    }
    if (expenses.length === 0 && incomes.length === 0) {
      dispatch(setError({ show: true, message: "Παρακαλώ προσθέστε τουλάχιστον μία εγγραφή εσόδων ή εξόδων." }));
      return;
    }

    dispatch(setLoading(true));
    const reportData = {
      expenses,
      incomes,
      totalExpenses: parseFloat(calculateTotal(expenses)),
      totalIncomes: parseFloat(calculateTotal(incomes)),
      total: parseFloat(calculateNetTotal()),
    };

    try {
      if (key) {
        await update(ref(database, `daily-reports/${key}`), reportData);
      } else {
        await set(ref(database, `daily-reports/${date}`), reportData);
        setIsDateReadOnly(true);
      }
      dispatch(setLoading(false));
    } catch (error) {
      dispatch(setLoading(false));
      dispatch(
        setError({ show: true, message: "Παρουσιάστηκε σφάλμα κατά την αποθήκευση του απολογισμού." + error.message }),
      );
    }
  };

  const handleConfirmDeleteQuestion = () => {
    if (key) {
      navigate("/daily-reports");
      remove(ref(database, `daily-reports/${key}`), {});
    }
    setShowQuestion(false);
  };

  const Section = ({ title, color, items, type }) => (
    <div className="mb-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className={`font-semibold ${color}`}>{title}</h3>
        <Button variant="outline" className="!px-3 !py-1.5 text-sm" onClick={() => openModal(type)}>
          <FaPlus size={12} /> Προσθήκη
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <button
            key={index}
            type="button"
            onClick={() => openModal(type, item, index)}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-left active:scale-[0.99]"
          >
            <span className="truncate text-slate-700">{item.description}</span>
            <span className="flex shrink-0 items-center gap-3">
              <span className="font-semibold text-slate-800">{eur(item.amount)}</span>
              <RiEdit2Fill className="text-brand-500" size={18} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageHeader title="Απολογισμός Ημέρας">
        {key && (
          <MdDelete className="cursor-pointer text-rose-500" size={24} onClick={() => setShowDeleteQuestion(true)} />
        )}
      </PageHeader>

      <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden px-4">
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          <Input
            floatAlways
            type="date"
            label="Ημ/νία"
            value={date}
            onChange={(e) => checkDateSubmission(e.target.value)}
            required
            disabled={isDateReadOnly}
            className="mb-3"
          />

          <div className="mb-3 grid grid-cols-3 gap-2 rounded-2xl bg-white p-3 shadow-card">
            <div className="text-center">
              <div className="text-xs text-slate-400">Έσοδα</div>
              <div className="flex items-center justify-center gap-1 font-semibold text-emerald-600">
                <FaArrowUp size={11} /> {eur(calculateTotal(incomes))}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-400">Έξοδα</div>
              <div className="flex items-center justify-center gap-1 font-semibold text-rose-500">
                <FaArrowDown size={11} /> {eur(calculateTotal(expenses))}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-400">Καθαρά</div>
              <div className="font-bold text-brand-700">{eur(calculateNetTotal())}</div>
            </div>
          </div>

          <Section title="Έξοδα" color="text-rose-600" items={expenses} type="expense" />
          <Section title="Έσοδα" color="text-emerald-600" items={incomes} type="income" />
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-brand-600/10 py-3">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Επιστροφή
          </Button>
          <Button type="submit">Αποθήκευση</Button>
        </div>
      </form>

      <Modal
        open={showModal}
        onClose={closeModal}
        title={`${modalType === "expense" ? "Έξοδα" : "Έσοδα"}`}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Ακύρωση
            </Button>
            {modalIndex !== null && (
              <Button variant="danger" onClick={deleteItem}>
                Διαγραφή
              </Button>
            )}
            <Button onClick={handleSaveItem}>Αποθήκευση</Button>
          </>
        }
      >
        <div className="space-y-3 py-1">
          <Input
            label="Περιγραφή"
            value={modalData.description}
            onChange={(e) => handleModalInputChange("description", e.target.value)}
          />
          <Input
            label="Ποσό"
            type="number"
            inputMode="decimal"
            value={modalData.amount}
            onChange={(e) => handleModalInputChange("amount", e.target.value)}
          />
        </div>
      </Modal>

      <Question
        show={showQuestion}
        onClose={() => setShowQuestion(false)}
        onConfirm={handleConfirmQuestion}
        message="Αυτή η ημερομηνία έχει ήδη υποβληθεί. Θέλετε να δείτε την αναφορά;"
      />
      <Question
        show={showDeleteQuestion}
        onClose={() => setShowDeleteQuestion(false)}
        onConfirm={handleConfirmDeleteQuestion}
        message="Επιβεβαίωση διαγραφής"
      />
    </div>
  );
};

export default DailyReportForm;
